import { Link } from "react-router-dom";
import { LogIn, Instagram, Facebook, Youtube, MessageCircle } from "lucide-react";
import analogLogoWordmark from "@/assets/analog-logo-wordmark.webp";
import { COLORS, typography } from "@/styles/may-theme";
import FilmGrainOverlay from "./FilmGrainOverlay";
import { useAuth } from "@/hooks/useAuth";
import { POST_EVENT } from "@/config/postEvent";

const MayFooter = () => {
  const { user, isAdmin } = useAuth();
  // Post-event: editorial/archive navigation only. No ticket, lodging,
  // giveaway, booking or waitlist calls to action.
  const navItems: { label: string; path: string }[] = [
    { label: 'Story', path: '/story' },
    { label: 'Lineup', path: '/lineup' },
    { label: 'Experience', path: '/experience' },
    { label: 'Schedule', path: '/schedule' },
    { label: 'Wine Camp', path: '/winecamp' },
    { label: '2026 Passes (archive)', path: '/tickets' },
    { label: '2026 Lodging (archive)', path: '/stay' },
  ];
  const siteLinks = [
    { label: 'Getting Here', path: '/getting-here' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Get Involved', path: '/get-involved' },
    { label: 'Mixtape 🎧', path: '/mixtape' },
    { label: 'Terms', path: '/terms' },
    { label: 'Privacy', path: '/privacy' },
    { label: 'Contact', path: '/contact' },
  ];


  const handleOpenChat = () => {
    window.dispatchEvent(new CustomEvent('openSupportChat'));
  };
  const socialLinks = [
    { icon: Instagram, url: 'https://www.instagram.com/analogreunion', label: 'Instagram' },
    { icon: Facebook, url: 'https://www.facebook.com/cosmicofest', label: 'Facebook' },
    { icon: Youtube, url: 'https://www.youtube.com/@LaunchPadlptv', label: 'YouTube' },
  ];

  return (
    <footer className="relative" style={{ backgroundColor: COLORS.charcoal }}>
      <div className="grid grid-cols-1 md:grid-cols-2">
        
        {/* Left Panel: Logo and Info */}
        <div 
          className="relative p-8 md:p-12 lg:p-16 flex flex-col justify-between min-h-[300px]"
          style={{ backgroundColor: COLORS.dustySky }}
        >
          <FilmGrainOverlay opacity={0.5} />
          
          <Link to="/" className="relative z-10 inline-block group">
            <img 
              src={analogLogoWordmark} 
              alt="Analog" 
              className="h-10 md:h-12 w-auto transition-all duration-300 group-hover:opacity-70 group-hover:scale-[1.02]" 
              style={{ filter: 'brightness(0) saturate(0)' }} 
            />
          </Link>
          
          <div className="relative z-10 mt-8">
            <p style={{ ...typography.body, color: COLORS.charcoal, fontSize: '14px', lineHeight: 1.7, maxWidth: '280px', opacity: 0.8 }}>
              {POST_EVENT.endedNotice}
            </p>
          </div>
          
          <div className="relative z-10 mt-6">
            <p
              data-testid="nonprofit-disclosure"
              style={{ ...typography.body, color: COLORS.charcoal, fontSize: '12px', lineHeight: 1.6, maxWidth: '320px', opacity: 0.85 }}
            >
              {POST_EVENT.nonprofitDisclosure}{' '}
              <a 
                href="https://lp.foundation/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline hover:opacity-70 transition-opacity"
                style={{ color: COLORS.charcoal }}
              >
                Learn about Launch Pad Foundation
              </a>
            </p>
          </div>

          
          <div className="relative z-10 mt-6">
          <p style={{ ...typography.caption, color: COLORS.boulder, letterSpacing: '0.1em', fontSize: '10px' }}>
              © {new Date().getFullYear()} ANALOG REUNION
            </p>
          </div>
        </div>
        
        {/* Right Panel: Navigation */}
        <div 
          className="relative p-8 md:p-12 lg:p-16 flex flex-col justify-between min-h-[300px]"
          style={{ backgroundColor: COLORS.charcoal }}
        >
          <FilmGrainOverlay opacity={0.5} />
          
          <div className="relative z-10">
            <p style={{ ...typography.caption, color: COLORS.boulder, letterSpacing: '0.15em', fontSize: '11px' }}>
              NAVIGATE
            </p>
          </div>
          
          <nav className="relative z-10 mt-8 flex flex-col gap-3">
          {navItems.map((item) => (
              <Link 
                key={item.path}
                to={item.path}
                className="transition-opacity hover:opacity-70"
                style={{ 
                  ...typography.body, 
                  color: COLORS.dustySky,
                  fontSize: '15px',
                }}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          
          <div className="relative z-10 mt-6">
            <Link
              to="/my-tickets"
              data-testid="past-attendee-link"
              className="inline-flex items-center justify-center px-5 py-3 mb-5 transition-opacity hover:opacity-70"
              style={{
                color: COLORS.boulder,
                ...typography.caption,
                letterSpacing: '0.05em',
                fontSize: '11px',
                paddingLeft: 0,
              }}
            >
              {POST_EVENT.pastAttendeeLabel}
            </Link>

            <p style={{ ...typography.caption, color: COLORS.boulder, letterSpacing: '0.15em', fontSize: '10px', marginBottom: '8px' }}>
              MORE
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {siteLinks.map((link) => (
                <Link 
                  key={link.path}
                  to={link.path}
                  className="transition-opacity hover:opacity-70"
                  style={{ 
                    ...typography.caption, 
                    color: COLORS.boulder,
                    fontSize: '11px',
                    letterSpacing: '0.05em',
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            {/* Social Media Icons */}
            <div className="flex items-center gap-4 mt-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-opacity hover:opacity-70"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4" style={{ color: COLORS.boulder }} />
                </a>
              ))}
              <a
                href="https://cschultz.substack.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-opacity hover:opacity-70"
                aria-label="Substack"
              >
                <svg 
                  className="w-4 h-4" 
                  style={{ color: COLORS.boulder }} 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                >
                  <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24l9.54-5.645L20.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/>
                </svg>
              </a>
            </div>
            
            {/* Need Help link */}
            <button
              onClick={handleOpenChat}
              className="flex items-center gap-1.5 mt-4 transition-opacity hover:opacity-70"
              style={{ 
                ...typography.caption, 
                color: COLORS.boulder,
                fontSize: '11px',
                letterSpacing: '0.05em',
              }}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Need Help?
            </button>
          </div>
          
          <div className="relative z-10 mt-8 flex items-center justify-between">
            <p style={{ ...typography.caption, color: COLORS.boulder, letterSpacing: '0.1em', fontSize: '10px' }}>
              MAY 15–17, 2026 · HEALDSBURG, CA
            </p>
            
            {isAdmin ? (
              <Link 
                to="/admin"
                className="transition-opacity hover:opacity-70"
                style={{ ...typography.caption, color: COLORS.boulder, letterSpacing: '0.1em', fontSize: '10px' }}
              >
                ADMIN
              </Link>
            ) : user ? (
              <Link 
                to="/admin-setup"
                className="transition-opacity hover:opacity-70"
                style={{ ...typography.caption, color: COLORS.boulder, letterSpacing: '0.1em', fontSize: '10px' }}
              >
                SETUP
              </Link>
            ) : (
              <Link 
                to="/auth"
                className="transition-opacity hover:opacity-70"
                aria-label="Sign In"
              >
                <LogIn className="w-4 h-4" style={{ color: COLORS.boulder }} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MayFooter;
